import React from 'react';
import { SlideInMenuVariant } from './SlideInMenu.config';
import {
  useComputeSlideInMenuState,
  useSlideInMenuControlls,
} from './SlideInMenu.hooks';
import { SlideInMenuProps } from './SlideInMenu.type';
import CompactSlideInMenu from './partials/CompactSlideInMenu.component';
import ExpandedSlideInMenu from './partials/ExpandedSlideInMenu.component';

export default function SlideInMenu({
  navbarContent,
  variant,
}: SlideInMenuProps) {
  const { closeSlideInMenu } = useSlideInMenuControlls();
  const refOfPreviousFocusOwner = React.useRef<HTMLElement | null>(null);
  const menuContentWrapperRef = React.useRef<HTMLDivElement>(null);

  const menuState = useComputeSlideInMenuState();

  // Manages focus.
  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (!menuContentWrapperRef.current) {
      return;
    }

    if (!menuState.isOpen) {
      timeoutId = setTimeout(() => {
        if (typeof refOfPreviousFocusOwner.current?.focus === 'function') {
          refOfPreviousFocusOwner.current?.focus();
        }

        refOfPreviousFocusOwner.current = null;
      }, 200);
      return;
    }

    refOfPreviousFocusOwner.current = document.activeElement as HTMLElement;

    timeoutId = setTimeout(() => {
      const anchor =
        menuContentWrapperRef.current?.querySelector(
          'a[aria-current="page"]',
        ) || null;

      if (anchor) {
        (anchor as HTMLAnchorElement)?.focus();
      }
    }, 200);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [menuState.isOpen]);

  switch (variant) {
    case SlideInMenuVariant.expanded:
      return (
        <ExpandedSlideInMenu
          ref={menuContentWrapperRef}
          {...menuState}
          navbarContent={navbarContent}
          onClickOutside={closeSlideInMenu}
        />
      );
    case SlideInMenuVariant.compact:
      return (
        <CompactSlideInMenu
          ref={menuContentWrapperRef}
          {...menuState}
          navbarContent={navbarContent}
          onClickOutside={closeSlideInMenu}
        />
      );
  }
}
